import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Radio, Plus, Trash2, Loader2, Rss, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { useToast } from "../hooks/use-toast";

export default function NewsFeedsEditor() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", url: "", region: "US" });
  const [refreshMinutes, setRefreshMinutes] = useState(30);
  const [refreshBusy, setRefreshBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [srcs, s] = await Promise.all([
        api.get("/admin/news-sources"),
        api.get("/settings"),
      ]);
      setBuiltin(srcs.data.builtin || []);
      setCustom(srcs.data.custom || []);
      const rm = s.data?.news_refresh_minutes ?? s.data?.news_cache_ttl_minutes ?? 30;
      setRefreshMinutes(Number(rm) || 30);
    } catch {
      toast({ title: "Failed to load feeds", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const saveRefreshInterval = async () => {
    const v = Math.max(1, Math.min(24 * 60, Number(refreshMinutes) || 30));
    setRefreshBusy(true);
    try {
      // PUT /settings expects the full settings doc; fetch → patch → put keeps other fields intact.
      const { data: current } = await api.get("/settings");
      await api.put("/settings", { ...current, news_refresh_minutes: v, news_cache_ttl_minutes: v });
      setRefreshMinutes(v);
      toast({ title: `Feeds will refresh every ${v} minute${v === 1 ? "" : "s"}` });
    } catch {
      toast({ title: "Couldn't save interval", variant: "destructive" });
    } finally { setRefreshBusy(false); }
  };

  const toggle = async (source) => {
    // Optimistic
    if (source.builtin) {
      setBuiltin((cur) => cur.map((s) => s.key === source.key ? { ...s, enabled: !s.enabled } : s));
    } else {
      setCustom((cur) => cur.map((s) => s.key === source.key ? { ...s, enabled: !s.enabled } : s));
    }
    try {
      await api.put(`/admin/news-sources/${encodeURIComponent(source.key)}/enabled`, { enabled: !source.enabled });
    } catch (e) {
      toast({ title: "Toggle failed", variant: "destructive" });
      load();
    }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/admin/news-sources/${encodeURIComponent(key)}`);
      toast({ title: `Removed feed ${key}` });
      load();
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const add = async () => {
    if (!form.key.trim() || !form.url.trim() || !form.label.trim()) {
      toast({ title: "Key, label and URL are required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/news-sources", {
        key: form.key.trim().toLowerCase(),
        label: form.label.trim(),
        url: form.url.trim(),
        region: form.region.trim() || "—",
        enabled: true,
      });
      toast({ title: "Feed added" });
      setForm({ key: "", label: "", url: "", region: "US" });
      load();
    } catch (e) {
      toast({ title: "Add failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const allSources = [...builtin, ...custom];

  return (
    <Card data-testid="news-feeds-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Radio className="w-4 h-4 text-primary" /> Industry News feeds</CardTitle>
        <CardDescription>Toggle live security advisory feeds shown on the Industry Alerts page, or add your own.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Refresh interval — drives the background news refresh loop and the top-nav counter */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 bg-muted/20">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold">Refresh interval</div>
                <div className="text-[11.5px] text-muted-foreground">
                  How often the server re-fetches every feed and updates the Industry Alerts top-nav counter. Applies immediately.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number"
                  min={1}
                  max={1440}
                  value={refreshMinutes}
                  onChange={(e) => setRefreshMinutes(e.target.value)}
                  className="w-24 h-9 text-right"
                  data-testid="news-refresh-interval-input"
                />
                <span className="text-[12px] text-muted-foreground">min</span>
                <Button
                  size="sm"
                  onClick={saveRefreshInterval}
                  disabled={refreshBusy}
                  className="gap-1.5"
                  data-testid="news-refresh-interval-save"
                >
                  <Save className="w-3.5 h-3.5" /> {refreshBusy ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>

            {/* Sources list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {allSources.map((s) => (
                <div
                  key={s.key}
                  className="flex items-start gap-3 rounded-lg border border-border p-3 bg-background"
                  data-testid={`feed-row-${s.key}`}
                >
                  <Switch checked={!!s.enabled} onCheckedChange={() => toggle(s)} data-testid={`feed-toggle-${s.key}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[13.5px]">{s.label}</span>
                      <span className="text-[11px] text-muted-foreground">· {s.region || "—"}</span>
                      {s.builtin ? (
                        <span className="text-[10px] uppercase tracking-wider font-semibold rounded-full bg-primary/10 text-primary px-1.5 py-0.5">Built-in</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-semibold rounded-full bg-muted text-muted-foreground px-1.5 py-0.5">Custom</span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      <span className="font-mono">key:</span> {s.key} · <span className="opacity-70">{s.url}</span>
                    </div>
                  </div>
                  {!s.builtin && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(s.key)} data-testid={`feed-delete-${s.key}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Add form */}
            <div className="rounded-lg border border-dashed border-border p-3.5 bg-muted/20">
              <div className="flex items-center gap-2 mb-2.5">
                <Rss className="w-4 h-4 text-primary" />
                <div className="font-semibold text-[13.5px]">Add a custom feed</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                <div>
                  <Label className="text-[11.5px]">Key</Label>
                  <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. trellix" data-testid="new-feed-key" />
                </div>
                <div>
                  <Label className="text-[11.5px]">Label</Label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Trellix Advisories" data-testid="new-feed-label" />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <Label className="text-[11.5px]">Region</Label>
                  <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="US / CA / EU" data-testid="new-feed-region" />
                </div>
                <div className="sm:col-span-2 lg:col-span-4">
                  <Label className="text-[11.5px]">RSS / Atom URL</Label>
                  <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://example.com/feed.xml" data-testid="new-feed-url" />
                </div>
              </div>
              <div className="mt-2.5">
                <Button onClick={add} disabled={busy} className="gap-1.5" data-testid="new-feed-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add feed
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
